/*
 * Copyright (c) Microsoft Corporation. All rights reserved.
 * Licensed under the MIT License.
 */

import { LogLevel } from "@azure/msal-browser";
import configEnv from "./environment";

/**
 * Configuration object to be passed to MSAL instance on creation.
 * For a full list of MSAL.js configuration parameters, visit:
 * https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-browser/docs/configuration.md
 */

const redirectURL = configEnv.REDIRECT_URL;
const isLocalhostRedirect = redirectURL?.includes("localhost");

export const msalConfig = {
  auth: {
    clientId: configEnv.CLIENT_ID,
    authority: "https://login.microsoftonline.com/" + configEnv.TENANT_ID,
    redirectUri: redirectURL,
  },
  cache: {
    cacheLocation: isLocalhostRedirect ? "localStorage" : "sessionStorage",
    storeAuthStateInCookie: false, // Set this to "true" if you are having issues on IE11 or Edge
  },
  system: {
    loggerOptions: {
      logLevel: LogLevel.Error,
      loggerCallback: (level, message, containsPii) => {
        if (containsPii) {
          return;
        }
        switch (level) {
          case LogLevel.Error:
            console.error(message);
            return;
          case LogLevel.Info:
            console.info(message);
            return;
          case LogLevel.Verbose:
            console.debug(message);
            return;
          case LogLevel.Warning:
            console.warn(message);
            return;
          default:
            return;
        }
      },
    },
  },
};

/**
 * Scopes you add here will be prompted for user consent during sign-in.
 * By default, MSAL.js will add OIDC scopes (openid, profile, email) to any login request.
 * For more information about OIDC scopes, visit:
 * https://docs.microsoft.com/en-us/azure/active-directory/develop/v2-permissions-and-consent#openid-connect-scopes
 */
export const loginRequest = {
  // Select the actual configured registration, not Vite's local DEV flag.
  // Local development currently uses the production API and registration.
  scopes: [`api://${configEnv.CLIENT_ID}/access_as_user`],
};
